<template>
  <div>
    <breadcrumb-header
      :items="breadcrumbs"
      help-link="/docs/publisher/after-shifts/experiences"
    >
      <template #append>
        <v-spacer />
        <div
          v-if="can('submit_experience')"
          class="text-right align-top"
        >
          <v-btn
            v-if="smAndUp"
            color="secondary"
            prepend-icon="fas fa-plus"
            :text="t('experience.submit')"
            @click="openForm"
          />
        </div>
      </template>
    </breadcrumb-header>
    <v-btn
      v-if="!smAndUp"
      color="secondary"
      size="large"
      prepend-icon="fas fa-plus"
      :text="t('experience.submit')"
      @click="openForm"
    />
    <div v-if="!submittingExperience">
      <experience-card
        v-if="featuredExperience"
        :experience="featuredExperience"
        :featured="true"
        color="primary"
        class="my-4"
        @click="handleExperienceClick(featuredExperience)"
      />

      <v-divider />
      <div class="text-headline-medium my-1">
        {{ t('experience.more') }}
      </div>
      <v-divider class="mb-4" />
      <v-icon
        color="grey-darken-1"
        icon="fas fa-filter"
        class="me-2"
        size="x-small"
      />
      <v-btn
        v-for="(tag, index) in tags"
        :key="index"
        :text="tag"
        class="me-1 mb-1"
        size="small"
        :color="hasTagFilter(tag) ? 'primary-lighten-1 text-primary-darken-1' : 'light-grey text-font'"
        @click="toggleTagFilter(tag)"
      />
      <div>
        <v-text-field
          v-model.lazy="searchExperiences"
          clearable
          :label="t('global.search')"
          @update:model-value="reloadExperiences()"
          @click:clear="reloadExperiences()"
        >
          <template #prepend-inner>
            <v-icon
              icon="fas fa-search"
              size="x-small"
            />
          </template>
        </v-text-field>
      </div>

      <div class="mb-4">
        <div v-if="experiences && experiences.length">
          <v-row>
            <v-col
              v-for="experience in experiences"
              :key="experience.id"
              cols="12"
              md="6"
            >
              <experience-card
                color="primary-lighten-1"
                :experience="experience"
                :truncate="true"
                @click="handleExperienceClick(experience)"
              />
            </v-col>
          </v-row>
        </div>
        <v-alert
          v-else-if="experiences"
          :text="searchExperiences ? t('experience.no_matching_found') : t('experience.none_found')"
        />
        <div v-else>
          <div class="text-center">
            <loading-spinner />
          </div>
        </div>
      </div>
    </div>
    <v-dialog
      v-model="submittingExperience"
      :fullscreen="xs"
      max-width="600px"
    >
      <experience-submit-form
        v-if="submittingExperience"
        @close="closeForm"
      />
    </v-dialog>
    <v-dialog
      v-if="selectedExperience"
      v-model="showSelectedExperience"
      max-width="600px"
      :fullscreen="xs"
    >
      <experience-card
        color="primary"
        :experience="selectedExperience"
        closable
        @close="showSelectedExperience = false"
      />
    </v-dialog>
    <vuetify-data-table
      :headers="headers"
      :model-name="t('experience.title')"
      :url="searchUrl"
      :includes="['location', 'tags']"
      :additional-actions="additionalActions"
      :edit-enabled="false"
      :delete-enabled="false"
    />
  </div>
</template>

<script setup lang="ts">
import axios from 'axios';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useDisplay } from 'vuetify/lib/framework.mjs';
import { useDataTableUtilities, usePermissions } from '@/composables';
import { useErrorStore } from '@/stores/errors';
import VuetifyDataTable from '../crud/VuetifyDataTable.vue';
import TooltipButton from '../TooltipButton.vue';
import ExperienceCard from './ExperienceCard.vue';
import ExperienceSubmitForm from './ExperienceSubmitForm.vue';

const { t } = useI18n();
const headers = [
  {
    key: 'date',
    title: t('date.title'),
  },
  {
    key: 'title',
    title: t('global.title'),
  },
  {
    key: 'location_id',
    value: 'location.name',
    title: t('location.title'),
  },
  {
    key: 'actions',
    title: t('global.action', 2),
    sortable: false,
  },
];

const router = useRouter();

const additionalActions = [
  {
    component: TooltipButton,
    callback: viewExperience,
    name: t('global.read_more', 2),
    props: {
      text: t('global.read_more', 2),
      icon: 'fas fa-eye',
      size: 'small',
      color: 'primary',
      tooltip: t('global.read_more', 2),
    },
  },
];
const { handleError } = useErrorStore();
const { watchArray, getStringArrayFromQuery } = useDataTableUtilities();
const route = useRoute();
const selectedTags = ref<string[]>(getStringArrayFromQuery(route.query?.tags, []));

watchArray(selectedTags, 'tags');

const tags = ref([]);

function hasTagFilter(tag) {
  return selectedTags.value.includes(tag);
}

function toggleTagFilter(tag) {
  if (selectedTags.value.includes(tag)) {
    selectedTags.value = selectedTags.value.filter((l) => l !== tag);
  }
  else {
    selectedTags.value = [...selectedTags.value, tag];
  }
  reloadExperiences();
}

async function loadTags() {
  try {
    const response = await axios.get('/api/experiences/tags');
    tags.value = response.data.data;
  }
  catch (error) {
    handleError(error, t('shift_tag.cannot_load'));
  }
}

loadTags();

function viewExperience(data) {
  router.push({ path: `/experiences/${data.id}` });
}

const featuredExperience = ref<App.Models.Experience>();
const breadcrumbs = computed(() => [{ title: t('experience.title', 2) }]);
const experiences = ref<[App.Models.Experience]>();
const searchExperiences = ref('');
const selectedExperience = ref<App.Models.Experience>();
const showSelectedExperience = ref(false);
const submittingExperience = ref(false);

const { smAndUp, xs } = useDisplay();
const { can } = usePermissions();

async function loadFeaturedExperience() {
  try {
    const response = await axios.get('/api/experiences/featured?inc[]=location&inc[]=tags');
    featuredExperience.value = response.data.data;
  }
  catch (error) {
    handleError(error, t('experience.cannot_load'));
  }
}

const searchUrl = computed(() => {
  let url = '/api/experiences/published?inc[]=tags&inc[]=location&sort=-date';
  if (searchExperiences.value) {
    url += `&search=${searchExperiences.value}`;
  }
  selectedTags.value.forEach((tag) => {
    url += `&tags[]=${tag}`;
  });
  return url;
});
async function loadExperiences() {
  try {
    const response = await axios.get(searchUrl.value);
    experiences.value = response.data.data;
  }
  catch (error) {
    handleError(error, t('experience.cannot_load'));
  }
}
let experienceTimeout: ReturnType<typeof setTimeout> | null = null;
async function reloadExperiences() {
  experiences.value = undefined;
  if (experienceTimeout) {
    clearTimeout(experienceTimeout);
  }
  experienceTimeout = setTimeout(() => {
    loadExperiences();
  }, 500);
}
function openForm() {
  submittingExperience.value = true;
}
function closeForm() {
  submittingExperience.value = false;
}
function handleExperienceClick(experience: App.Models.Experience) {
  showSelectedExperience.value = true;
  selectedExperience.value = experience;
}
onMounted(() => {
  loadFeaturedExperience();
  loadExperiences();
});
</script>
